<template>
  <div
    v-if="!isBasketEmpty"
    class="o-checkout container -slim"
  >
    <div
      v-if="!isLoggedIn && !continueAsGuest"
      class="o-checkout__login-wrapper"
    >
      <login-box
        context="checkout"
        @continue-without-clicked="continueAsGuest = true"
      />
    </div>
    <div
      v-else
      class="row"
    >
      <div class="col-lg-8 o-checkout__fields-container">
        <div class="col-12">
          <billing-information v-model="billingInformation" :account-information-set="accountInformationSet" />
          <shipping-methods
            v-model="shippingInformation"
            :account-shipping-address="accountShippingAddress"
            :company-delivery-name="content.nzSettingsCompanyDeliveryName"
            :warehouse-pickup-name="content.nzSettingsWarehousePickupName"
            :warehouse-address="content.nzSettingsWarehouseAddress"
            :warehouse-postal-code="content.nzSettingsWarehousePostalCode"
            :warehouse-city="content.nzSettingsWarehouseCity"
            :warehouse-opening-hours="content.nzSettingsWarehouseOpeningHours"
            :warehouse-coordinates="content.nzSettingsWarehouseCoordinates"
            class="mt-5"
            @update-shipping-address="debounceShippingAddressChanged"
            @update-is-another-address-selected="updateIsAnotherAddressSelected"
            @reset-shipping-address="resetShippingAddress"
          />
          <payment-options
            v-model="paymentMethod"
            :credit-card-name="content.nzSettingsCreditCardName"
            :invoice-name="content.nzSettingsInvoiceName"
            :is-another-address-selected="isAnotherAddressSelected"
            class="mt-5"
          />
          <checkout-comment
            v-model="comment"
            class="mt-5"
          />
          <confirm-payment
            v-model="confirmPayment"
            :terms-and-conditions-page="content.termsAndConditionsPage"
            :marketing-terms-and-conditions-page="content.marketingTermsAndConditionsPage"
            :has-go-to-payment-been-clicked="hasGoToPaymentBeenClicked"
            class="mt-5"
          />
        </div>
        <div class="col-12 d-flex justify-content-center justify-content-lg-end">
          <button-with-loader
            :loading="isGoToCheckoutLoading"
            :text="checkoutSubmitText"
            :disabled="isGoToCheckoutLoading"
            class="-primary mt-7 mt-lg-9 py-4 px-12"
            @click.native="goToCheckout"
          />
        </div>
      </div>

      <div class="col-lg-4">
        <h2 class="mb-5">
          {{ $t('yourProducts', 'Dine varer') }}
        </h2>
        <mini-basket />
        <order-summary :summary="basket.orderSummary" :orderlines="basket.orderLines" :addons="basket.addons" />
        <discount-manual v-if="isLoggedIn && isImpersonating && isConsultant" />
        <!-- <voucher-code :vouchers="basket.vouchers" /> -->
      </div>

      <div class="col-12 d-flex d-lg-none justify-content-center">
        <button-with-loader
          :loading="isGoToCheckoutLoading"
          :text="checkoutSubmitText"
          :disabled="isGoToCheckoutLoading"
          class="-primary mt-7 mt-lg-9 py-4 px-12"
          @click.native="goToCheckout"
        />
      </div>
    </div>
  </div>
</template>

<script>
import MiniBasket from '@/components/organisms/ShopCommerce/MiniBasket';
import BillingInformation from '@/components/molecules/ShopCommerce/BillingInformation.vue';
import ShippingMethods from '@/components/molecules/ShopCommerce/ShippingMethods.vue';
import CheckoutComment from '@/components/molecules/ShopCommerce/CheckoutComment.vue';
import OrderSummary from '@/components/molecules/ShopCommerce/OrderSummary.vue';
import PaymentOptions from '@/components/molecules/ShopCommerce/PaymentOptions.vue';
// import VoucherCode from '@/components/molecules/ShopCommerce/VoucherCode.vue';
import DiscountManual from '@/components/molecules/ShopCommerce/DiscountManual.vue';
import ConfirmPayment from '@/components/molecules/ShopCommerce/ConfirmPayment.vue';
import ButtonWithLoader from '@/components/molecules/ButtonWithLoader.vue';
import { trackCheckout } from '@/utils/enhancedEcomTracking';
import { trackCheckoutStartedOnce, trackPaymentReturnOnce } from '@/utils/klaviyoTracking';
import contentMixin from '@/mixins/contentMixin';
import loginMixin from '@/mixins/loginMixin';
import debounce from 'debounce';
import LoginBox from '@/components/organisms/LoginBox.vue';

export default {
  name: 'Checkout',
  components: {
    MiniBasket,
    BillingInformation,
    ShippingMethods,
    CheckoutComment,
    OrderSummary,
    PaymentOptions,
    DiscountManual,
    // VoucherCode,
    ConfirmPayment,
    ButtonWithLoader,
    LoginBox,
  },
  mixins: [contentMixin, loginMixin],
  async fetch () {
    await this.getBasket();
  },
  data () {
    return {
      billingInformation: {
        email: '',
        fullName: '',
        phoneNumber: '',
        address: '',
        zipCode: '',
        city: '',
        companyCvrNumber: '',
        companyEanNumber: '',
        purchaseOrderNumber: ''
      },
      shippingInformation: {
        shippingMethod: '',
        shippingAddress: {
          parcelShopNumber: null,
          fullName: '',
          phoneNumber: '',
          address: '',
          zipCode: '',
          city: ''
        }
      },
      comment: '',
      isGoToCheckoutLoading: false,
      hasGoToPaymentBeenClicked: false,
      paymentMethod: '',
      confirmPayment: {
        isTermsAccepted: false,
        isMarketingTermsAccepted: false
      },
      isDefaultShippingAddress: false,
      isAnotherAddressSelected: false,
      continueAsGuest: false,
      basketReady: false,
      accountInformationSet: false,
      klaviyoCheckoutStartedTracked: false
    }
  },
  computed: {
    isConsultant () {
      return this.$store.getters['user/user/isConsultant']
    },
    currentCulture () {
      return this.$store.getters['site/getCurrentCulture'];
    },
    basket () {
      return this.$store.getters['cart/basket/getBasket'];
    },
    isBasketEmpty () {
      return this.$store.getters['cart/basket/isBasketEmpty'];
    },
    hasCustomerNumber () {
      return this.$store.getters['user/user/hasCustomerNumber'];
    },
    isImpersonating () {
      return this.$store.getters['user/user/isImpersonating'];
    },
    isBillingInformationValid () {
      const billingInformationFields = this.fields['$billing-information'];

      if (!billingInformationFields) {
        return false;
      }

      const isBillingInformationValid = Object.values(billingInformationFields).every(field => field.valid === true);
      return isBillingInformationValid;
    },
    isShippingAddressValid () {
      const shippingAddressFields = this.fields['$shipping'];

      /**
       * If no shipping fields is found the shipping is not entered manually and should be valid
       */
      if (!shippingAddressFields) {
        return true;
      }

      const isShippingAddressValid = Object.values(shippingAddressFields).every(field => field.valid === true);
      return isShippingAddressValid;
    },
    paymentOptions () {
      return this.$store.getters['checkout/checkout/getPaymentOptions'];
    },
    isInvoicePaymentPicked () {
      return this.paymentOptions.some(paymentOption => paymentOption.id.toString() === this.paymentMethod && paymentOption.name === this.content.nzSettingsInvoiceName);
    },
    checkoutSubmitText () {
      return this.isInvoicePaymentPicked ? this.$t('checkoutPlaceOrderText', 'Placér bestilling') : this.$t('goToPaymentPage', 'Gå til betaling');
    },
    accountBillingInformation () {
      return {
        email: this.user?.email ?? '',
        fullName: (this.user?.properties?.firstName ?? '') + (this.user?.properties?.lastName ? ' ' + this.user?.properties?.lastName : ''),
        phoneNumber: this.user?.accountProperties?.phoneNo ?? '',
        address: this.user?.accountProperties?.address ?? '',
        zipCode: this.user?.accountProperties?.postCode ?? '',
        city: this.user?.accountProperties?.city ?? '',
        companyName: (this.user?.accountProperties?.accountName ?? '') + (this.user?.accountProperties?.accountName2 ? ' ' + this.user?.accountProperties?.accountName2 : ''),
        companyCvrNumber: this.user.accountProperties?.vatNumber ?? '',
        companyEanNumber: this.user.accountProperties?.eANNo ?? '',
        purchaseOrderNumber: ''
      }
    },
    accountShippingAddress () {
      return {
        fullName: (this.user?.accountProperties?.shipToName ?? '') + (this.user?.accountProperties?.shipToName2 ? '{split}' + this.user?.accountProperties?.shipToName2 : ''),
        address: this.user?.accountProperties.shipToAddress ?? '',
        zipCode: this.user?.accountProperties?.shipToPostCode ?? '',
        city: this.user?.accountProperties?.shipToCity ?? '',
      }
    },
    hasAccountShippingAddress () {
      return (
        this.accountShippingAddress.fullName !== '' &&
        this.accountShippingAddress.address !== '' &&
        this.accountShippingAddress.zipCode !== '' &&
        this.accountShippingAddress.city !== ''
      )
    }
  },
  watch: {
    hasCustomerNumber: {
      deep: true,
      immediate: true,
      async handler () {
        if(this.hasCustomerNumber) {
          this.billingInformation = this.accountBillingInformation;
          this.accountInformationSet = true;
        }

        
        if(this.hasCustomerNumber && !this.$isNotNullOrUndefined(this.getCustomPropertyValue('memberId'))) {
          this.getBasket(this.user.id);
        }
        
      }
    },
    billingInformation: {
      deep: true,
      immediate: true,
      async handler () {
        this.debounceBillingInformationChanged();
        this.trackCheckoutStarted();
      }
    },
    basket: {
      deep: true,
      handler () {
        this.trackCheckoutStarted();
      }
    },
    'confirmPayment.isMarketingTermsAccepted' (accepted) {
      if (!accepted && process.client) {
        sessionStorage.setItem('klaviyoTrackingAllowed', 'false');
      }
    }
  },
  mounted () {
    // Klaviyo tracking
    trackCheckout(this.basket?.orderLines);
    this.trackCheckoutStarted();
    this.trackPaymentCancelled();
  },
  methods: {
    trackCheckoutStarted () {
      console.debug('[Klaviyo] Checkout Started check', {
        alreadyTracked: this.klaviyoCheckoutStartedTracked,
        orderLines: this.basket?.orderLines?.length || 0,
        email: this.checkoutTrackingEmail(),
        route: this.$route?.fullPath
      });

      if (this.klaviyoCheckoutStartedTracked || !process.client || sessionStorage.getItem('klaviyoTrackingAllowed') !== 'true' || !this.basket?.orderLines?.length) {
        return;
      }

      const request = trackCheckoutStartedOnce(this, this.basket, this.$route?.fullPath, this.checkoutTrackingEmail());
      console.debug('[Klaviyo] Checkout Started request', { started: !!request });
      if (request) {
        this.klaviyoCheckoutStartedTracked = true;
      }
    },
    checkoutTrackingEmail () {
      return this.user?.email || this.basket?.billingAddress?.email || this.billingInformation?.email || null;
    },
    async trackPaymentCancelled () {
      if (this.$route?.query?.orderGuid) {
        let order = this.basket;

        try {
          order = await this.$repositories.order.byId(this.currentCulture, this.$route.query.orderGuid);
        } catch (error) {
          console.debug('[Klaviyo] Payment Cancelled order lookup failed', { orderGuid: this.$route.query.orderGuid, error });
        }

        trackPaymentReturnOnce(this, 'Payment Cancelled', this.$route.query.orderGuid, 'cancelled', order, this.checkoutTrackingEmail());
      }
    },
    getCustomPropertyValue (propertyName) {

      if (typeof this.basket?.customProperties === 'object') {
        let item = this.basket?.customProperties[propertyName]
        return item
      } else {
        let item = this.basket?.customProperties?.find(x => x.name === propertyName && x.value !== '')
        return item?.value
      }
    },
    async getBasket (userId) {
      await this.$store.dispatch('cart/basket/fetchBasket', {
        culture: this.currentCulture,
        userId: userId
      });


      /* I believe that this is whats missing in the mapping of companyName */
      if (this.$isNotNullOrUndefined(this.basket.billingAddress)) {
        this.billingInformation = {
          ...this.billingInformation,
          email: this.basket.billingAddress?.email,
          fullName: `${this.basket.billingAddress?.firstName} ${this.basket.billingAddress?.lastName}`,
          phoneNumber: this.basket.billingAddress?.telephone,
          address: this.basket.billingAddress?.streetName,
          zipCode: this.basket.billingAddress?.zipCode,
          city: this.basket.billingAddress?.cityName
        };
      }

      if(this.$isNotNullOrUndefined(this.basket.shippingAddress)) {
        this.shippingInformation.shippingAddress = {
          address: this.basket.shippingAddress?.streetName,
          zipCode: this.basket.shippingAddress?.zipCode,
          city: this.basket.shippingAddress?.cityName
        };
      }

      if (this.$isNotNullOrUndefined(this.basket?.customProperties?.['purchase order'])) {
        this.billingInformation = {
          ...this.billingInformation,
          purchaseOrderNumber: this.basket.customProperties['purchase order'],
        };
      }

      if (this.$isNotNullOrUndefined(this.basket?.customProperties?.['cvr number'])) {
        this.billingInformation = {
          ...this.billingInformation,
          companyCvrNumber: this.basket.customProperties['cvr number'],
        };
      }

      // In case user is impersonating
      if (this.hasCustomerNumber) {
        this.billingInformation = this.accountBillingInformation;
        this.accountInformationSet = true;
      }

      if (this.basket?.shippingInfo?.shippingMethodId) {
        this.shippingInformation.shippingMethod = this.basket.shippingInfo.shippingMethodId.toString();
      }

      if (this.$isNotNullOrUndefined(this.basket?.customProperties?.comment)) {
        this.comment = this.basket.customProperties.comment;
      }

      this.basketReady = true;
      this.trackCheckoutStarted();
    },
    debounceBillingInformationChanged: debounce(function () {
      if (!this.isBillingInformationValid) {
        return;
      }

      this.$store.dispatch('cart/basket/setBillingAddress', {
        culture: this.currentCulture,
        billingInformation: this.billingInformation,
      });
      this.$repositories.basket.setProperty(this.currentCulture, 'purchase order', this.billingInformation.purchaseOrderNumber);
      this.$repositories.basket.setProperty(this.currentCulture, 'cvr number', this.billingInformation.companyCvrNumber);

      if (this.isDefaultShippingAddress) {
        this.$store.dispatch('checkout/checkout/setShippingAddress', {
          culture: this.currentCulture,
          shippingAddress: (
            this.hasAccountShippingAddress ? this.accountShippingAddress : this.billingInformation
          )
        });
      }
    }, 1000),
    debounceShippingAddressChanged: debounce(function (isDefaultShippingAddress = false) {
      this.isDefaultShippingAddress = isDefaultShippingAddress;
      if (this.isDefaultShippingAddress && !this.isBillingInformationValid) {
        return;
      }

      // If using shipping address and it's not valid then return
      if (!this.isDefaultShippingAddress && !this.isShippingAddressValid) {
        return;
      }

      this.$store.dispatch('checkout/checkout/setShippingAddress', {
        culture: this.currentCulture,
        shippingAddress: (
          isDefaultShippingAddress && this.hasAccountShippingAddress ? this.accountShippingAddress :
          isDefaultShippingAddress && !this.hasAccountShippingAddress ? this.billingInformation :
          this.shippingInformation.shippingAddress
        )
      });

    }, 1000),
    async goToCheckout () {
      this.hasGoToPaymentBeenClicked = true;
      this.isGoToCheckoutLoading = true;

      /**
       * I'm aware that there is a validateAll.
       * However validateAll is only validating all "scopeless" fields
       * https://github.com/logaretm/vee-validate/issues/1564#issuecomment-417385665
       */
      const allFieldsValid = await this.$validator.validate();

      if (!allFieldsValid) {
        /**
         * Could we do this easier?
         */
        const yOffset = -125;
        const fieldName = this.errors.items[0].field;
        const element = document.getElementById(fieldName);
        const y = element?.getBoundingClientRect().top + window.pageYOffset + yOffset;

        window.scrollTo({top: y, behavior: 'smooth'});

        this.isGoToCheckoutLoading = false;
        return;
      }

      if (!this.confirmPayment.isTermsAccepted || !this.paymentMethod) {
        this.isGoToCheckoutLoading = false;
        return;
      }

      if (this.isLoggedIn && this.hasCustomerNumber) {
        await this.$repositories.basket.setProperty(this.currentCulture, 'Customer number', this.user.properties.customerNo);
      }

      if (this.isLoggedIn && this.isConsultant) {
        let userInitials = this.user?.properties?.salesPerson;
        if (userInitials) {
          await this.$repositories.basket.setProperty(this.currentCulture, 'Sales person', userInitials.toLowerCase());
        }
      }

      await this.$repositories.basket.setProperty(this.currentCulture, 'Comment', this.comment);
      await this.$store.dispatch('checkout/checkout/setPaymentMethod', {
        culture: this.currentCulture,
        paymentMethod: this.paymentMethod
      });

      if (this.confirmPayment.isMarketingTermsAccepted) {
        await this.subscribeKlaviyo();
        this.trackCheckoutStarted();
        this.$trackKlaviyoActiveOnSite?.();
      }

      const paymentUrl = await this.$store.dispatch('checkout/checkout/fetchPaymentUrl');
      if (paymentUrl) {
        window.location = process.env.ucommerceUrl + paymentUrl;
        return;
      }

      this.isGoToCheckoutLoading = false;
    },
    async subscribeKlaviyo () {
      try {
        const b = this.billingInformation;
        const user = this.$store.getters['user/user/getUser'];
        
        await this.$repositories.klaviyo.subscribe({
          email: user?.email || user?.Email || user?.properties?.email || b?.email,
          emailConsent: true,
          smsConsent: false,
          phoneNumber: b?.phoneNumber || null,
          firstName: b?.fullName || null,
          lastName: user?.lastName || null,
          salutation: user?.salutation || null,
          clubAccount: user?.clubAccount || null,
          b2c: user?.b2c || null,
          b2b: user?.b2b || null,
          organization: b?.companyName || null,
          address1: b?.address || null,
          address2: b?.address2 || null,
          city: b?.city || null,
          country: b?.country || null,
          region: b?.region || null,
          zip: b?.zipCode || null,
          timezone: user?.timezone || null,
          language: this.$store.getters['site/getCurrentCulture'] || null,
          source: 'Checkout',
          first_name: b?.fullName || null,
          last_name: user?.lastName || null,
          locale: this.$store.getters['site/getCurrentCulture'] || null
        });

        if (process.client) {
          sessionStorage.setItem('klaviyoTrackingAllowed', 'true');
        }

      } catch (e) {
      }
    },
    resetShippingAddress () {
      this.shippingInformation.shippingAddress = {
        parcelShopNumber: null,
        fullName: '',
        phoneNumber: '',
        address: '',
        zipCode: '',
        city: ''
      };
    },
    updateIsAnotherAddressSelected (param) {
      this.isAnotherAddressSelected = param;
    }
  }
}
</script>
